import { useContext } from 'react'

import { DepositBanner } from '@awazevr/design-system.components.deposit-banner'
import { WishlistButton } from '@awazevr/design-system.components.wishlist-button'
import {
  MicroPropertyCard,
  MicroPropertyCardSkeleton,
} from '@awazevr/design-system.compositions.micro-property-card'
import { useIntlContext } from '@awazevr/mfe-common.contexts.intl'
import { useSearchContextState } from '@awazevr/mfe-common.contexts.search'
import {
  DataLayerEventType,
  DataLayerFactory,
  TmsContext,
} from '@awazevr/mfe-common.contexts.tms'
import { Regions } from '@awazevr/mfe-common.types-and-mocks.one-search-client'
import type {
  CommonSite,
  ICommonAggregations,
} from '@awazevr/mfe-common.types-and-mocks.site-search-service'

import { PRICE_FROM_NIGHTS } from '@/constants'
import { Assert } from '@/utils/assert'
import { dataLayerErrorHandler } from '@/utils/dataLayerErrorHandler'

import {
  buildMapImpressionAndClickPayload,
  buildPropertyUrlForMapProperties,
  handlePropertyImages,
} from './DynamicMap.utils'

import { WishlistButtonContainer } from './DynamicMap.styles'

interface MapContentProps {
  wishlistButtonVisible: boolean
  showOfferBanner: CommonSite | null
  isDatedSearch: boolean
  wishlistButtonProps: {
    isChecked: boolean
    onClickCallback: () => void
  }
  pdpUrlSegment: string
  depositDisplayAmount: string | null
  showDepositTerms: boolean | null
  isPropertyInfoError: boolean
  propertyInfo: CommonSite | null
}

export const MapContent = ({
  wishlistButtonVisible,
  showOfferBanner,
  isDatedSearch,
  wishlistButtonProps,
  pdpUrlSegment,
  depositDisplayAmount,
  showDepositTerms,
  isPropertyInfoError,
  propertyInfo,
}: MapContentProps): JSX.Element => {
  const tms = useContext(TmsContext)

  const { formatCurrency, formatMessage } = useIntlContext()
  const { searchCriteria, aggregations, rhsLevel, total } =
    useSearchContextState()

  Assert.defined(searchCriteria, 'SearchCriteria must be defined')

  const handlePropertyClick = (): void => {
    new DataLayerFactory()
      .getEvent(DataLayerEventType.MapProductClick)
      .update(
        tms,
        buildMapImpressionAndClickPayload(
          searchCriteria,
          aggregations as ICommonAggregations,
          rhsLevel as Regions,
          total as number,
          propertyInfo as CommonSite,
        ),
        dataLayerErrorHandler,
      )
  }

  const { url, altText } = handlePropertyImages(propertyInfo)

  const ActiveDepositSchemeComponent = showOfferBanner ? (
    <DepositBanner
      highlightedMessage={`${formatMessage({
        id: 'search-results.property-list.reserve-today',
        defaultMessage: 'Reserve today for only',
      })} ${depositDisplayAmount}`}
      termsMessage={
        showDepositTerms
          ? formatMessage({
              id: 'search-results.property-list.reserve-for-selected-properties',
              defaultMessage: 'on selected properties',
            })
          : ''
      }
      highlightedFontSize={100}
    />
  ) : undefined

  if (propertyInfo) {
    return (
      <>
        {wishlistButtonVisible && (
          <WishlistButtonContainer>
            <WishlistButton
              label="Add to Wishlist"
              variant="elevated"
              showLabel={false}
              {...wishlistButtonProps}
            />
          </WishlistButtonContainer>
        )}
        <MicroPropertyCard
          displayValues={{
            imageURL: url,
            imageAlt: altText,
            title: propertyInfo.displayName ?? '',
            grade: Number(propertyInfo.grade ?? 0),

            // TODO fix types in Design system:
            // these four properties cascade down to the SpecItem component
            // that actually will actually behave differently at runtime
            // if the value is undefined - therefore this
            // component should accept an undefined value for these props
            guests: propertyInfo.property?.sleeps as number,
            bedrooms: propertyInfo.property?.bedrooms as number,
            bathrooms: propertyInfo.property?.bathrooms as number,
            pets: propertyInfo.property?.pets as number,
            pdpURL: buildPropertyUrlForMapProperties(
              pdpUrlSegment,
              propertyInfo,
              searchCriteria,
            ),
            onPropertyLinkClick: () => handlePropertyClick(),
            ...(propertyInfo.price &&
              !propertyInfo.priceFrom && {
                price: formatCurrency(propertyInfo.price),
              }),
            ...(propertyInfo.wasPrice && {
              wasPrice: formatCurrency(propertyInfo.wasPrice),
            }),
            ...(propertyInfo.priceFrom && {
              fromPrice: formatCurrency(propertyInfo.priceFrom),
            }),
          }}
          translations={{
            moreInfo: formatMessage({
              id: 'general.more-info',
              defaultMessage: 'More info',
            }),
            from: formatMessage({
              id: 'general.from',
              defaultMessage: 'From',
            }),
            fromNights: `${isDatedSearch ? searchCriteria.nights : PRICE_FROM_NIGHTS} ${formatMessage(
              {
                id: 'search-results.property-list.nights',
                defaultMessage: `nights`,
              },
            )}`,
            outOf: formatMessage({
              id: 'site.out-of',
              defaultMessage: 'Out of',
            }),
            stars: formatMessage({
              id: 'site.stars',
              defaultMessage: 'Stars',
            }),
            propertySpecTranslations: {
              guests: {
                singular: formatMessage({
                  id: 'property-specs.guests.singular',
                  defaultMessage: 'dfsfs',
                }),
                plural: formatMessage({
                  id: 'property-specs.guests.plural',
                  defaultMessage: 'ggggggg',
                }),
              },
              studio: formatMessage({
                id: 'site.studio',
                defaultMessage: 'Studio',
              }),
              bedrooms: {
                singular: formatMessage({
                  id: 'site.bedrooms.singular',
                  defaultMessage: 'Bedroom',
                }),
                plural: formatMessage({
                  id: 'site.bedrooms.plural',
                  defaultMessage: 'Bedrooms',
                }),
              },
              bathrooms: {
                singular: formatMessage({
                  id: 'site.bathrooms.singular',
                  defaultMessage: 'Bathroom',
                }),
                plural: formatMessage({
                  id: 'site.bathrooms.plural',
                  defaultMessage: 'Bathrooms',
                }),
              },
              pets: {
                singular: formatMessage({
                  id: 'site.pets.singular',
                  defaultMessage: 'Pet',
                }),
                plural: formatMessage({
                  id: 'site.bathrooms.plural',
                  defaultMessage: 'Pets',
                }),
                label: formatMessage({
                  id: 'site.up-to',
                  defaultMessage: 'Up to',
                }),
                allowed: formatMessage({
                  id: 'site.pets.allowed',
                  defaultMessage: 'Allowed',
                }),
              },
            },
          }}
          {...(showOfferBanner && {
            offerBanner: ActiveDepositSchemeComponent,
          })}
        />
      </>
    )
  } else if (isPropertyInfoError) {
    return (
      <div>
        {formatMessage({
          id: 'placeholder-for-error-msg',
          defaultMessage: 'There has been an error',
        })}
      </div>
    )
  } else {
    return <MicroPropertyCardSkeleton />
  }
}
